import { sql } from "drizzle-orm";
import { serializeWorkflowValue } from "@gencow/core";
import type { WorkflowV2DbLike, WorkflowV2RunRow } from "./workflow-kernel-v2-store.js";
import {
  collectWorkflowV2ErrorCodes,
  collectWorkflowV2ErrorMessages,
  executeWorkflowV2Query,
  isTransientWorkflowV2DbError,
} from "./workflow-kernel-v2-db.js";
import { workflowV2Jsonb } from "./workflow-kernel-v2-json.js";

function isMissingWorkflowV1ReadModelError(error: unknown): boolean {
  const codes = new Set(collectWorkflowV2ErrorCodes(error));
  const message = collectWorkflowV2ErrorMessages(error).join("\n");
  return (
    codes.has("42P01") ||
    (codes.has("23503") && message.includes("_gencow_workflow_steps")) ||
    message.includes('relation "_gencow_workflows" does not exist') ||
    message.includes("relation _gencow_workflows does not exist") ||
    message.includes('relation "_gencow_workflow_steps" does not exist') ||
    message.includes("relation _gencow_workflow_steps does not exist")
  );
}

async function ignoreMissingV1ReadModel(run: () => Promise<void>): Promise<void> {
  try {
    await run();
  } catch (error) {
    if (!isMissingWorkflowV1ReadModelError(error) && !isTransientWorkflowV2DbError(error)) throw error;
  }
}

function messageFromErrorJson(error: unknown): string {
  if (error && typeof error === "object" && typeof (error as { message?: unknown }).message === "string") {
    return (error as { message: string }).message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export async function mirrorWorkflowV2RunClaimed(db: WorkflowV2DbLike, run: WorkflowV2RunRow): Promise<void> {
  await ignoreMissingV1ReadModel(async () => {
    await executeWorkflowV2Query(
      db,
      sql`
      UPDATE _gencow_workflows
      SET
        status = 'running',
        error = NULL,
        error_code = NULL,
        retry_count = ${run.retry_count},
        first_claimed_at = COALESCE(first_claimed_at, NOW()),
        handler_started_at = COALESCE(handler_started_at, NOW()),
        last_resume_attempt_at = NOW(),
        updated_at = NOW(),
        completed_at = NULL
      WHERE id = ${run.id}
    `,
    );
  });
}

export async function mirrorWorkflowV2StepRunning(options: {
  db: WorkflowV2DbLike;
  runId: string;
  stepKey: string;
}): Promise<void> {
  await ignoreMissingV1ReadModel(async () => {
    await executeWorkflowV2Query(
      options.db,
      sql`
      INSERT INTO _gencow_workflow_steps (
        workflow_id,
        step_name,
        status,
        output,
        error,
        started_at,
        updated_at,
        completed_at
      )
      VALUES (
        ${options.runId},
        ${options.stepKey},
        'running',
        NULL,
        NULL,
        NOW(),
        NOW(),
        NULL
      )
      ON CONFLICT (workflow_id, step_name)
      DO UPDATE SET
        status = 'running',
        error = NULL,
        started_at = COALESCE(_gencow_workflow_steps.started_at, NOW()),
        updated_at = NOW(),
        completed_at = NULL
    `,
    );
    await executeWorkflowV2Query(
      options.db,
      sql`
      UPDATE _gencow_workflows
      SET
        status = 'running',
        current_step = ${options.stepKey},
        error = NULL,
        error_code = NULL,
        updated_at = NOW()
      WHERE id = ${options.runId}
    `,
    );
  });
}

export async function mirrorWorkflowV2StepCompleted(options: {
  db: WorkflowV2DbLike;
  runId: string;
  stepKey: string;
  output: unknown;
}): Promise<void> {
  const persisted = serializeWorkflowValue(options.output);
  await ignoreMissingV1ReadModel(async () => {
    await executeWorkflowV2Query(
      options.db,
      sql`
      UPDATE _gencow_workflow_steps
      SET
        status = 'completed',
        output = ${workflowV2Jsonb(persisted)},
        error = NULL,
        updated_at = NOW(),
        completed_at = NOW()
      WHERE workflow_id = ${options.runId}
        AND step_name = ${options.stepKey}
    `,
    );
  });
}

export async function mirrorWorkflowV2StepFailed(options: {
  db: WorkflowV2DbLike;
  runId: string;
  stepKey: string;
  error: unknown;
}): Promise<void> {
  const message = messageFromErrorJson(options.error);
  await ignoreMissingV1ReadModel(async () => {
    await executeWorkflowV2Query(
      options.db,
      sql`
      UPDATE _gencow_workflow_steps
      SET
        status = 'failed',
        error = ${message},
        updated_at = NOW(),
        completed_at = NULL
      WHERE workflow_id = ${options.runId}
        AND step_name = ${options.stepKey}
    `,
    );
    await executeWorkflowV2Query(
      options.db,
      sql`
      UPDATE _gencow_workflows
      SET
        status = 'running',
        current_step = ${options.stepKey},
        error = ${message},
        updated_at = NOW()
      WHERE id = ${options.runId}
    `,
    );
  });
}

export async function mirrorWorkflowV2RunPending(options: {
  db: WorkflowV2DbLike;
  runId: string;
  stepKey: string | null;
  retryCount?: number;
  error?: unknown;
  errorCode?: string | null;
}): Promise<void> {
  const message = options.error == null ? null : messageFromErrorJson(options.error);
  await ignoreMissingV1ReadModel(async () => {
    await executeWorkflowV2Query(
      options.db,
      sql`
      UPDATE _gencow_workflows
      SET
        status = 'pending',
        current_step = ${options.stepKey},
        retry_count = COALESCE(${options.retryCount ?? null}, retry_count),
        error = ${message},
        error_code = ${options.errorCode ?? null},
        updated_at = NOW(),
        completed_at = NULL
      WHERE id = ${options.runId}
    `,
    );
  });
}

export async function mirrorWorkflowV2RunCompleted(options: {
  db: WorkflowV2DbLike;
  runId: string;
  result: unknown;
}): Promise<void> {
  const persisted = serializeWorkflowValue(options.result);
  await ignoreMissingV1ReadModel(async () => {
    await executeWorkflowV2Query(
      options.db,
      sql`
      UPDATE _gencow_workflows
      SET
        status = 'completed',
        result = ${workflowV2Jsonb(persisted)},
        error = NULL,
        error_code = NULL,
        current_step = NULL,
        completed_at = NOW(),
        updated_at = NOW()
      WHERE id = ${options.runId}
    `,
    );
  });
}

export async function mirrorWorkflowV2RunFailed(options: {
  db: WorkflowV2DbLike;
  runId: string;
  retryCount: number;
  error: unknown;
  errorCode: string | null;
}): Promise<void> {
  const message = messageFromErrorJson(options.error);
  await ignoreMissingV1ReadModel(async () => {
    await executeWorkflowV2Query(
      options.db,
      sql`
      UPDATE _gencow_workflows
      SET
        status = 'failed',
        error = ${message},
        error_code = ${options.errorCode},
        retry_count = ${options.retryCount},
        completed_at = NOW(),
        updated_at = NOW()
      WHERE id = ${options.runId}
    `,
    );
  });
}
